/**
 * Formatador de SQL no estilo "river" (próximo do padrão do DataGrip),
 * usado no editor de views de integração.
 *
 * Características do estilo (extraídas dos dumps reais em
 * verso-api/database/materialized_views/*.sql):
 *   - Palavras-chave de cláusula (SELECT, FROM, JOIN, ON, WHERE, AND, OR,
 *     GROUP BY, ORDER BY, ...) alinhadas à DIREITA, formando um "rio": a borda
 *     direita de cada keyword cai na mesma coluna. Frases multi-palavra
 *     (LEFT JOIN, GROUP BY) transbordam para a esquerda do rio.
 *   - O conteúdo começa 1 coluna após o rio.
 *   - Lista do SELECT: um item por linha, com os AS de todas as colunas
 *     alinhados pela maior expressão.
 *   - Vírgula no fim da linha.
 *   - Subconsultas (SELECT ...) são formatadas recursivamente e alinhadas
 *     logo após o parêntese de abertura.
 *
 * Implementação tolerante a dialetos (Oracle, SQL Server, Postgres, MySQL):
 * é um re-indentador baseado em lexer, não um parser estrito — não rejeita
 * sintaxe específica de ERP (ex.: `(+)`, `||`, `NVL`, `DECODE`).
 *
 * Exporta `formatRiver(sql)` (e `tokenize`/`mesmosTokensSignificativos`,
 * reaproveitados pela validação de comentários e pelos testes).
 *
 * Rede de segurança: após formatar, a saída é re-tokenizada e comparada com a
 * entrada (incluindo comentários). Se qualquer token divergir — perda, ganho ou
 * reordenação — o SQL original é devolvido intacto.
 */

// Palavras reservadas (apenas para uppercase e detecção de chamada de função).
const KEYWORDS = new Set([
  "SELECT", "DISTINCT", "ALL", "FROM", "WHERE", "AND", "OR", "NOT", "IN", "IS",
  "NULL", "LIKE", "ILIKE", "BETWEEN", "EXISTS", "AS", "ON", "USING", "JOIN",
  "INNER", "LEFT", "RIGHT", "FULL", "OUTER", "CROSS", "GROUP", "ORDER", "BY",
  "HAVING", "LIMIT", "OFFSET", "FETCH", "UNION", "INTERSECT", "EXCEPT", "CASE",
  "WHEN", "THEN", "ELSE", "END", "ASC", "DESC", "WITH", "OVER", "PARTITION",
  "INTO", "VALUES", "SET", "ROWS", "ONLY", "NULLS", "FIRST", "LAST",
]);

// ---------------------------------------------------------------------------
// Lexer
// ---------------------------------------------------------------------------

const MULTI_OPS = ["||", "<=", ">=", "<>", "!=", "::", "(+)"];

export function tokenize(sql) {
  const tokens = [];
  let i = 0;
  const n = sql.length;

  const isWordStart = (c) => /[A-Za-z_]/.test(c);
  const isWordChar = (c) => /[A-Za-z0-9_$#]/.test(c);

  while (i < n) {
    const c = sql[i];

    // espaços em branco -> descartados (o formatador reconstrói o espaçamento)
    if (/\s/.test(c)) { i++; continue; }

    // comentário de linha
    if (c === "-" && sql[i + 1] === "-") {
      let j = i;
      while (j < n && sql[j] !== "\n") j++;
      tokens.push({ type: "lcomment", value: sql.slice(i, j) });
      i = j;
      continue;
    }

    // comentário de bloco (`fechado: false` sinaliza /* sem */ até o fim)
    if (c === "/" && sql[i + 1] === "*") {
      let j = i + 2;
      while (j < n && !(sql[j] === "*" && sql[j + 1] === "/")) j++;
      const fechado = j < n;
      j = Math.min(j + 2, n);
      tokens.push({ type: "bcomment", value: sql.slice(i, j), fechado });
      i = j;
      continue;
    }

    // string com aspas simples (escapes: '' padrão SQL; \' e \\ estilo MySQL)
    if (c === "'") {
      let j = i + 1;
      while (j < n) {
        if (sql[j] === "\\" && (sql[j + 1] === "'" || sql[j + 1] === "\\")) { j += 2; continue; }
        if (sql[j] === "'" && sql[j + 1] === "'") { j += 2; continue; }
        if (sql[j] === "'") { j++; break; }
        j++;
      }
      tokens.push({ type: "string", value: sql.slice(i, j) });
      i = j;
      continue;
    }

    // identificador entre aspas duplas
    if (c === '"') {
      let j = i + 1;
      while (j < n && sql[j] !== '"') j++;
      j = Math.min(j + 1, n);
      tokens.push({ type: "word", value: sql.slice(i, j), kw: false });
      i = j;
      continue;
    }

    // número
    if (/[0-9]/.test(c) || (c === "." && /[0-9]/.test(sql[i + 1]))) {
      let j = i;
      while (j < n && /[0-9.]/.test(sql[j])) j++;
      tokens.push({ type: "num", value: sql.slice(i, j) });
      i = j;
      continue;
    }

    // palavra / identificador / keyword
    if (isWordStart(c)) {
      let j = i;
      while (j < n && isWordChar(sql[j])) j++;
      const raw = sql.slice(i, j);
      const up = raw.toUpperCase();
      const kw = KEYWORDS.has(up);
      tokens.push({ type: "word", value: kw ? up : raw, kw });
      i = j;
      continue;
    }

    // operadores multi-caractere
    let matched = null;
    for (const op of MULTI_OPS) {
      if (sql.startsWith(op, i)) { matched = op; break; }
    }
    if (matched) {
      tokens.push({ type: "op", value: matched });
      i += matched.length;
      continue;
    }

    // pontuação / operador simples
    if (c === "(") { tokens.push({ type: "lparen", value: "(" }); i++; continue; }
    if (c === ")") { tokens.push({ type: "rparen", value: ")" }); i++; continue; }
    if (c === ",") { tokens.push({ type: "comma", value: "," }); i++; continue; }
    if (c === ".") { tokens.push({ type: "dot", value: "." }); i++; continue; }
    if (c === ";") { tokens.push({ type: "semi", value: ";" }); i++; continue; }

    tokens.push({ type: "op", value: c });
    i++;
  }

  return tokens;
}

const isWord = (t, v) => t && t.type === "word" && t.value.toUpperCase() === v;

// ---------------------------------------------------------------------------
// Utilitários de tokens
// ---------------------------------------------------------------------------

// Índice do parêntese que fecha o aberto em `open`.
function matchParen(tokens, open) {
  let depth = 0;
  for (let i = open; i < tokens.length; i++) {
    if (tokens[i].type === "lparen") depth++;
    else if (tokens[i].type === "rparen") { depth--; if (depth === 0) return i; }
  }
  return tokens.length - 1;
}

// Divide tokens (no nível 0 de parênteses) sempre que `pred(token, index)`
// indicar o início de um separador. Retorna lista de { sep, body }.
function splitTopLevel(tokens, matchSep) {
  const parts = [];
  let cur = { sep: null, body: [] };
  let depth = 0;
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (t.type === "lparen") depth++;
    else if (t.type === "rparen") depth--;

    let sep = null;
    if (depth === 0) sep = matchSep(tokens, i);
    if (sep) {
      if (cur.sep !== null || cur.body.length) parts.push(cur);
      cur = { sep: sep.kw, body: [] };
      i += sep.len - 1;
      continue;
    }
    cur.body.push(t);
  }
  if (cur.sep !== null || cur.body.length) parts.push(cur);
  return parts;
}

// ---------------------------------------------------------------------------
// Renderização de expressões (inline, com tratamento de subconsultas)
// ---------------------------------------------------------------------------

// Decide se há espaço entre `prev` e `cur` ao reconstruir uma expressão.
function spaceBetween(prev, cur) {
  if (!prev) return false;
  const cv = cur.value;
  const pv = prev.value;

  // nunca espaço antes destes
  if (cur.type === "comma" || cur.type === "dot" || cur.type === "rparen" ||
      cv === "::" || cur.type === "semi") return false;
  // nunca espaço depois destes
  if (prev.type === "dot" || prev.type === "lparen") return false;
  // operador (+) do Oracle cola no identificador anterior
  if (cv === "(+)") return false;

  if (cur.type === "lparen") {
    // chamada de função: nome(  -> sem espaço
    if (prev.type === "word" && !prev.kw) return false;
    if (prev.type === "rparen") return true;
    if (pv === "(") return false;
    return true; // IN (, EXISTS (, = (, etc.
  }

  return true;
}

// Largura máxima de linha antes de quebrar uma chamada de função/CASE longa.
const MAX_COL = 110;

// Renderiza tokens inline, apenas reconstruindo o espaçamento. Exceção: após
// um comentário de linha (--) a quebra é OBRIGATÓRIA — sem ela, qualquer token
// seguinte viraria parte do comentário e o SQL sairia corrompido.
function inlineExpr(tokens) {
  let s = "";
  let prev = null;
  for (const t of tokens) {
    if (t.type === "lcomment") {
      if (s.length && !s.endsWith("\n")) s += " ";
      s += t.value + "\n";
      prev = null;
      continue;
    }
    if (spaceBetween(prev, t)) s += " ";
    s += t.value;
    prev = t;
  }
  return s;
}

function inlineWidth(tokens) {
  const linhas = inlineExpr(tokens).split("\n");
  return Math.max(...linhas.map((l) => l.length));
}

// Índice do END que fecha o CASE aberto em `start` (considera CASE aninhado).
function matchCaseEnd(tokens, start) {
  let d = 0;
  for (let i = start; i < tokens.length; i++) {
    if (isWord(tokens[i], "CASE")) d++;
    else if (isWord(tokens[i], "END")) { d--; if (d === 0) return i; }
  }
  return tokens.length - 1;
}

// Conta os ramos WHEN diretos de um CASE (ignora CASE aninhado).
function countWhens(caseTokens) {
  let c = 0, d = 0;
  for (const t of caseTokens) {
    if (isWord(t, "CASE")) d++;
    else if (isWord(t, "END")) d--;
    else if (d === 1 && isWord(t, "WHEN")) c++;
  }
  return c;
}

// Há construção que força quebra (comentário de linha, CASE com muitos ramos
// ou subconsulta)?
function hasBreaker(tokens) {
  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i].type === "lcomment") return true;
    if (isWord(tokens[i], "CASE")) {
      const e = matchCaseEnd(tokens, i);
      if (countWhens(tokens.slice(i, e + 1)) > 3) return true;
    }
    if (tokens[i].type === "lparen" && tokens[i + 1] &&
        (isWord(tokens[i + 1], "SELECT") || isWord(tokens[i + 1], "WITH"))) return true;
  }
  return false;
}

// Divide os argumentos de uma chamada (tokens internos do parêntese) por vírgula.
function splitArgs(tokens) {
  const out = [];
  let cur = [], depth = 0;
  for (const t of tokens) {
    if (t.type === "lparen") depth++;
    else if (t.type === "rparen") depth--;
    if (depth === 0 && t.type === "comma") { out.push(cur); cur = []; }
    else cur.push(t);
  }
  if (cur.length || out.length) out.push(cur);
  return out;
}

// Renderiza um CASE ... END em múltiplas linhas (WHEN/ELSE/END indentados +4).
function renderCase(caseTokens, startCol) {
  let i = 1, d = 1;
  const head = [];
  while (i < caseTokens.length) {
    const t = caseTokens[i];
    if (isWord(t, "CASE")) d++;
    else if (isWord(t, "END")) d--;
    if (d === 1 && isWord(t, "WHEN")) break;
    head.push(t);
    i++;
  }
  const branches = [];
  while (i < caseTokens.length) {
    const t = caseTokens[i];
    if (d === 1 && isWord(t, "END")) break;
    if (d === 1 && (isWord(t, "WHEN") || isWord(t, "ELSE"))) {
      const kw = t.value.toUpperCase();
      const seg = [];
      i++;
      while (i < caseTokens.length) {
        const u = caseTokens[i];
        if (isWord(u, "CASE")) d++;
        else if (isWord(u, "END")) { if (d === 1) break; d--; }
        if (d === 1 && (isWord(u, "WHEN") || isWord(u, "ELSE"))) break;
        seg.push(u);
        i++;
      }
      branches.push({ kw, toks: seg });
    } else {
      i++;
    }
  }
  const lines = [];
  lines.push("CASE" + (head.length ? " " + inlineExpr(head) : ""));
  const ind = " ".repeat(startCol + 4);
  for (const b of branches) {
    if (!b.toks.length) {
      lines.push(ind + b.kw);
      continue;
    }
    // renderExpr (e não inlineExpr): um comentário de linha (--) dentro do ramo
    // precisa quebrar a linha, senão o restante do ramo vira comentário.
    const blk = renderExpr(b.toks, ind.length + b.kw.length + 1);
    lines.push(ind + b.kw + " " + blk.lines[0]);
    for (let k = 1; k < blk.lines.length; k++) lines.push(blk.lines[k]);
  }
  lines.push(ind + "END");
  return { lines };
}

/**
 * Renderiza uma lista de tokens como expressão. `startCol` é a coluna absoluta
 * (dentro do bloco atual) onde a expressão começa — usada para alinhar
 * subconsultas/CASE/argumentos quebrados. Retorna { lines: [string] }, onde a
 * PRIMEIRA linha não tem recuo (o chamador a posiciona em startCol) e as demais
 * já vêm com o recuo absoluto.
 */
function renderExpr(tokens, startCol) {
  const lines = [];
  let line = "";
  let prev = null;
  const flush = () => { lines.push(line); line = ""; };
  const absCol = () => (lines.length === 0 ? startCol : 0) + line.length;
  const sp = (t) => (spaceBetween(prev, t) ? " " : "");

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];

    // ---- CASE ... END (quebra se tiver muitos ramos ou ficar muito longo) ----
    if (isWord(t, "CASE")) {
      const end = matchCaseEnd(tokens, i);
      const caseToks = tokens.slice(i, end + 1);
      const startCol2 = absCol() + (spaceBetween(prev, t) ? 1 : 0);
      if (countWhens(caseToks) > 3 || startCol2 + inlineWidth(caseToks) > MAX_COL) {
        line += sp(t);
        const blk = renderCase(caseToks, startCol2);
        line += blk.lines[0];
        if (blk.lines.length > 1) {
          flush();
          for (let k = 1; k < blk.lines.length - 1; k++) lines.push(blk.lines[k]);
          line = blk.lines[blk.lines.length - 1];
        }
        prev = caseToks[caseToks.length - 1]; // END
        i = end;
        continue;
      }
      // senão: segue para o append normal (inline)
    }

    // ---- parênteses ----
    if (t.type === "lparen") {
      const next = tokens[i + 1];

      // subconsulta: ( SELECT ... ) ou ( WITH ... )
      if (next && (isWord(next, "SELECT") || isWord(next, "WITH"))) {
        const close = matchParen(tokens, i);
        const sub = tokens.slice(i + 1, close);
        line += sp(t) + "(";
        const colAfter = absCol();
        const subLines = formatTokens(sub).split("\n");
        line += subLines[0];
        if (subLines.length > 1) {
          flush();
          for (let k = 1; k < subLines.length - 1; k++) lines.push(" ".repeat(colAfter) + subLines[k]);
          line = " ".repeat(colAfter) + subLines[subLines.length - 1];
        }
        line += ")";
        prev = { type: "rparen", value: ")" };
        i = close;
        continue;
      }

      // chamada de função / agrupamento com possível quebra de argumentos
      const close = matchParen(tokens, i);
      const inner = tokens.slice(i + 1, close);
      const callToks = tokens.slice(i, close + 1);
      const needBreak = inner.length > 0 &&
        (hasBreaker(inner) || absCol() + inlineWidth(callToks) > MAX_COL);
      if (needBreak) {
        line += sp(t) + "(";
        const parenCol = absCol();
        const args = splitArgs(inner);
        for (let a = 0; a < args.length; a++) {
          const argToks = args[a];
          if (a > 0) line += ",";
          const argBreaks = hasBreaker(argToks) || parenCol + inlineWidth(argToks) > MAX_COL;
          if (argBreaks) {
            flush();
            const blk = renderExpr(argToks, parenCol);
            lines.push(" ".repeat(parenCol) + blk.lines[0]);
            for (let k = 1; k < blk.lines.length; k++) lines.push(blk.lines[k]);
            line = lines.pop();
          } else {
            if (a > 0) line += " ";
            line += inlineExpr(argToks);
          }
        }
        line += ")";
        prev = { type: "rparen", value: ")" };
        i = close;
        continue;
      }
      // senão: segue para o append normal (inline)
    }

    // comentário de linha quebra a linha após si; a continuação volta
    // alinhada à coluna inicial da expressão
    if (t.type === "lcomment") {
      if (line.trim().length) line += " ";
      line += t.value;
      flush();
      line = " ".repeat(startCol);
      prev = null;
      continue;
    }

    line += sp(t) + t.value;
    prev = t;
  }

  // não empurra uma última linha só de indentação (sobra de lcomment no fim)
  if (line.trim().length || lines.length === 0) flush();
  return { lines };
}

// Renderiza expressão como string única (junta linhas).
function exprText(tokens, startCol) {
  return renderExpr(tokens, startCol).lines.join("\n");
}

// Coluna absoluta onde termina a última linha de uma expressão (para o AS).
function exprEndCol(lines, startCol) {
  return lines.length === 1 ? startCol + lines[0].length : lines[lines.length - 1].length;
}

// ---------------------------------------------------------------------------
// Detecção de cláusulas
// ---------------------------------------------------------------------------

// Tenta casar uma frase de JOIN começando em i. Retorna { kw, len } ou null.
function matchJoin(tokens, i) {
  const val = (k) => (tokens[i + k] && tokens[i + k].type === "word" ? tokens[i + k].value.toUpperCase() : null);
  const PREFIX = ["INNER", "LEFT", "RIGHT", "FULL", "CROSS"];
  let k = 0;
  const words = [];
  if (PREFIX.includes(val(0))) { words.push(val(0)); k++; }
  if (val(k) === "OUTER") { words.push(val(k)); k++; }
  if (val(k) === "JOIN") { words.push("JOIN"); k++; return { kw: words.join(" "), len: k }; }
  // JOIN puro
  if (val(0) === "JOIN") return { kw: "JOIN", len: 1 };
  return null;
}

// Separador de cláusulas de topo do SELECT.
function topClauseSep(tokens, i) {
  const t = tokens[i];
  if (!t || t.type !== "word") return null;
  const v = t.value.toUpperCase();
  const v2 = tokens[i + 1] && tokens[i + 1].type === "word" ? tokens[i + 1].value.toUpperCase() : null;

  if (v === "SELECT") return { kw: "SELECT", len: 1 };
  if (v === "FROM") return { kw: "FROM", len: 1 };
  if (v === "WHERE") return { kw: "WHERE", len: 1 };
  if (v === "HAVING") return { kw: "HAVING", len: 1 };
  if (v === "GROUP" && v2 === "BY") return { kw: "GROUP BY", len: 2 };
  if (v === "ORDER" && v2 === "BY") return { kw: "ORDER BY", len: 2 };
  if (v === "LIMIT") return { kw: "LIMIT", len: 1 };
  if (v === "OFFSET") return { kw: "OFFSET", len: 1 };
  const j = matchJoin(tokens, i);
  if (j) return j;
  return null;
}

// Divide um corpo de condição por AND/OR de nível 0, respeitando o
// `BETWEEN x AND y` (cujo AND não é separador de condição).
function splitConditions(tokens) {
  const parts = [];
  let cur = { sep: null, body: [] };
  let depth = 0;
  let betweenPending = 0;
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (t.type === "lparen") depth++;
    else if (t.type === "rparen") depth--;

    if (depth === 0 && t.type === "word") {
      const v = t.value.toUpperCase();
      if (v === "BETWEEN") {
        betweenPending++;
      } else if (v === "AND" && betweenPending > 0) {
        betweenPending--; // este AND pertence ao BETWEEN
      } else if (v === "AND" || v === "OR") {
        if (cur.sep !== null || cur.body.length) parts.push(cur);
        cur = { sep: v, body: [] };
        continue;
      }
    }
    cur.body.push(t);
  }
  if (cur.sep !== null || cur.body.length) parts.push(cur);
  return parts;
}

// Separador de UNION/INTERSECT/EXCEPT (nível 0).
function setOpSep(tokens, i) {
  const t = tokens[i];
  if (!t || t.type !== "word") return null;
  const v = t.value.toUpperCase();
  const v2 = tokens[i + 1] && tokens[i + 1].type === "word" ? tokens[i + 1].value.toUpperCase() : null;
  if (v === "UNION") return { kw: v2 === "ALL" ? "UNION ALL" : "UNION", len: v2 === "ALL" ? 2 : 1 };
  if (v === "INTERSECT" || v === "EXCEPT") return { kw: v, len: 1 };
  return null;
}

// ---------------------------------------------------------------------------
// Montagem das linhas do "rio"
// ---------------------------------------------------------------------------

// Constrói as "river lines" {kw, kind, body} de um SELECT simples (sem set-ops).
function buildRiverLines(tokens) {
  const clauses = splitTopLevel(tokens, topClauseSep);
  const lines = [];

  for (const cl of clauses) {
    const kw = cl.sep;
    if (!kw) {
      // tokens antes do primeiro SELECT (ex.: "CREATE VIEW x AS") — prefixo cru
      if (cl.body.length) lines.push({ kw: null, kind: "raw", body: cl.body });
      continue;
    }
    if (kw === "SELECT") {
      lines.push({ kw: "SELECT", kind: "select", body: cl.body });
    } else if (/JOIN$/.test(kw)) {
      // separa tabela ... ON condição
      const onParts = splitTopLevel(cl.body, (tk, i) =>
        isWord(tk[i], "ON") ? { kw: "ON", len: 1 } : null
      );
      const tablePart = onParts[0] && onParts[0].sep === null ? onParts[0].body : cl.body;
      lines.push({ kw, kind: "expr", body: tablePart });
      const onPart = onParts.find((p) => p.sep === "ON");
      if (onPart) pushConditions("ON", onPart.body, lines);
    } else if (kw === "WHERE" || kw === "HAVING") {
      pushConditions(kw, cl.body, lines);
    } else {
      // FROM, GROUP BY, ORDER BY, LIMIT, OFFSET -> conteúdo inline
      lines.push({ kw, kind: "expr", body: cl.body });
    }
  }
  return lines;
}

// Quebra um corpo de condição por AND/OR de nível 0 em várias river lines.
function pushConditions(headKw, body, lines) {
  const conds = splitConditions(body);
  let first = true;
  for (const c of conds) {
    const kw = first ? headKw : c.sep;
    first = false;
    lines.push({ kw, kind: "expr", body: c.body });
  }
}

// ---------------------------------------------------------------------------
// Renderização final de um conjunto de tokens (uma consulta, com set-ops)
// ---------------------------------------------------------------------------

function formatTokens(tokens) {
  // remove ; final
  while (tokens.length && tokens[tokens.length - 1].type === "semi") tokens = tokens.slice(0, -1);
  if (!tokens.length) return "";

  // set operations (UNION/INTERSECT/EXCEPT) no topo: cada consulta formatada
  // com seu próprio rio, separadas por uma linha em branco antes e depois do
  // operador (UNION ALL, etc.).
  const setParts = splitTopLevel(tokens, setOpSep);
  if (setParts.length > 1) {
    const blocks = [];
    for (const p of setParts) {
      if (p.sep) { blocks.push("", p.sep, ""); }
      blocks.push(renderQuery(p.body, computeRiver(p.body)));
    }
    return blocks.join("\n");
  }

  return renderQuery(tokens, computeRiver(tokens));
}

// Calcula a largura do rio (maior keyword de cláusula presente).
function computeRiver(tokens) {
  const lines = buildRiverLines(tokens);
  let r = 0;
  for (const l of lines) if (l.kw) r = Math.max(r, l.kw.length);
  return Math.max(r, 1);
}

function padKw(kw, river) {
  return " ".repeat(Math.max(0, river - kw.length)) + kw;
}

function renderQuery(tokens, river) {
  const lines = buildRiverLines(tokens);
  const out = [];
  const contentCol = river + 1; // 1 espaço após o rio

  for (const l of lines) {
    if (l.kind === "raw") {
      out.push(exprText(l.body, 0));
      continue;
    }
    if (l.kind === "select") {
      out.push(...renderSelect(l.body, river));
      continue;
    }
    // linha de expressão simples (FROM, JOIN, ON, WHERE, AND, GROUP BY, ...)
    const head = padKw(l.kw, river) + " ";
    const txt = exprText(l.body, contentCol);
    const txtLines = txt.split("\n");
    out.push(head + txtLines[0]);
    for (let k = 1; k < txtLines.length; k++) out.push(txtLines[k]);
  }
  return out.join("\n");
}

// Renderiza a lista do SELECT com os AS alinhados.
function renderSelect(body, river) {
  // DISTINCT logo após SELECT
  let prefix = "SELECT";
  let rest = body;
  if (rest[0] && isWord(rest[0], "DISTINCT")) {
    prefix = "SELECT DISTINCT";
    rest = rest.slice(1);
  } else if (rest[0] && isWord(rest[0], "ALL")) {
    prefix = "SELECT ALL";
    rest = rest.slice(1);
  }

  // SELECT é right-alinhado ao rio; as colunas começam logo após.
  const head = padKw(prefix, river);
  const contentCol = head.length + 1;

  const items = splitTopLevel(rest, (tk, i) =>
    tk[i].type === "comma" ? { kw: ",", len: 1 } : null
  ).map((p) => p.body).filter((b) => b.length);

  // Separa cada item em expr / alias (AS de nível 0) e já renderiza a expressão.
  const parsed = items.map((tokens) => {
    let asIdx = -1, depth = 0;
    for (let i = 0; i < tokens.length; i++) {
      if (tokens[i].type === "lparen") depth++;
      else if (tokens[i].type === "rparen") depth--;
      else if (depth === 0 && isWord(tokens[i], "AS")) { asIdx = i; break; }
    }
    const expr = asIdx === -1 ? tokens : tokens.slice(0, asIdx);
    const alias = asIdx === -1 ? null : tokens.slice(asIdx + 1);
    const lines = renderExpr(expr, contentCol).lines;
    return { lines, alias, endCol: exprEndCol(lines, contentCol) };
  });

  // Alvo de alinhamento do AS: a MAIOR coluna final entre os itens com alias.
  // Todos os AS são empurrados para essa coluna (inclusive os itens curtos e os
  // que terminam em "END)" de um CASE quebrado).
  let asTarget = 0;
  for (const p of parsed) if (p.alias) asTarget = Math.max(asTarget, p.endCol);

  const padLeft = " ".repeat(contentCol);
  const out = [];

  parsed.forEach((p, idx) => {
    const isLast = idx === parsed.length - 1;
    const comma = isLast ? "" : ",";
    const ln = p.lines.slice();
    let last = ln.pop();
    if (p.alias) {
      const pad = " ".repeat(Math.max(1, asTarget - p.endCol + 1));
      last += pad + "AS " + inlineExpr(p.alias) + comma;
    } else {
      last += comma;
    }
    ln.push(last);

    if (idx === 0) out.push(head + " " + ln[0]);
    else out.push(padLeft + ln[0]);
    for (let k = 1; k < ln.length; k++) out.push(ln[k]);
  });

  return out;
}

// ---------------------------------------------------------------------------
// API pública
// ---------------------------------------------------------------------------

// Assinatura dos tokens significativos de um SQL: tudo que o tokenizer devolve
// (identificadores, operadores, strings e TAMBÉM comentários), ignorando apenas
// whitespace e `;` finais (que o formatador descarta por definição).
function assinaturaTokens(sql) {
  const tokens = tokenize(String(sql || ""));
  while (tokens.length && tokens[tokens.length - 1].type === "semi") tokens.pop();
  return tokens.map((t) => `${t.type}:${t.value}`);
}

/**
 * True se os dois SQLs têm exatamente a mesma sequência de tokens
 * significativos (mesmo conteúdo, mesma ordem — só o whitespace pode variar).
 */
export function mesmosTokensSignificativos(sqlA, sqlB) {
  const a = assinaturaTokens(sqlA);
  const b = assinaturaTokens(sqlB);
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export function formatRiver(sql) {
  const input = String(sql || "");
  if (!input.trim()) return input;
  const tokens = tokenize(input);
  if (!tokens.length) return input;

  let out;
  try {
    out = formatTokens(tokens);
  } catch (e) {
    // fail-safe: qualquer erro de formatação devolve o SQL original
    return input;
  }

  // Rede de segurança: se a saída não re-tokeniza na MESMA sequência da
  // entrada (algo foi perdido, colado ou reordenado), devolve o original.
  return mesmosTokensSignificativos(input, out) ? out : input;
}

export default formatRiver;
